import { CdkScrollable } from '@angular/cdk/overlay';
import { Injectable, OnDestroy, Optional } from '@angular/core';

import { EMPTY, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { EmojiPickerService, EmojiSupportService } from '@recruitee/emoji';
import {
  BasicPlugin,
  BoldPlugin,
  ContextMenuPlugin,
  EditorButton,
  EmojiEditorPlugin,
  EmojiViewPlugin,
  ItalicPlugin,
  LinkEditPlugin,
  ListPlugin,
  MentionsPlugin,
  PluginsButtons,
  RtPMPlugin,
  IProsemirrorConfig,
  StrikethroughPlugin,
  UnderlinePlugin,
  SCHEMAS,
  RtSchema,
  PluginsEvents,
  ConfigPlugins,
} from '@recruitee/prosemirror';

import { ContextToolbarService } from '../components/context-toolbar/context-toolbar.service';
import { LinkComponentService } from '../components/link-input/link-component.service';
import { MentionsListService } from '../components/mentions/mentions-list.service';
import { ToolbarService } from '../components/toolbar/toolbar.service';
import { MentionsStream$, ScrollStream$ } from '../consts';

export type ProsemirrorDefaultConfigEvents = PluginsEvents<ConfigPlugins<ProsemirrorDefaultConfig>>;

@Injectable()
export class ProsemirrorDefaultConfig implements IProsemirrorConfig, OnDestroy {
  public eventStream$ = new Subject<ProsemirrorDefaultConfigEvents>();

  private events$ = new Subject<any>();
  private ngOnDestroy$ = new Subject();
  private mentionsDataSource$ = this.mentionsProvider$.pipe(takeUntil(this.ngOnDestroy$));
  private scroll$ = this.scrollStream$ || EMPTY;

  constructor(
    protected emojiSupportService: EmojiSupportService,
    protected mentionsProvider$: MentionsStream$,
    // This services are only used for editor mode
    @Optional() protected emojiPickerService?: EmojiPickerService,
    @Optional() protected toolbarService?: ToolbarService,
    @Optional() protected contextMenuService?: ContextToolbarService,
    @Optional() protected mentionsListService?: MentionsListService,
    @Optional() protected linkService?: LinkComponentService,
    @Optional() protected scrollStream$?: ScrollStream$,
  ) {
    this.events$
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(event => this.eventStream$.next(event));
  }

  public getSchema() {
    return SCHEMAS[RtSchema.Default]({
      mentionDataSource$: this.mentionsDataSource$,
    });
  }

  public getRenderPlugins(): RtPMPlugin[] {
    return [new EmojiViewPlugin(this.emojiSupportService)];
  }

  public getEditorPlugins() {
    return [
      new BasicPlugin(),
      new ContextMenuPlugin(this.contextMenuService!, this.scroll$),
      new EmojiEditorPlugin(this.events$, this.emojiPickerService!, this.scroll$),

      new BoldPlugin(),
      new ItalicPlugin(),
      new StrikethroughPlugin(),
      new UnderlinePlugin(),
      new LinkEditPlugin(this.linkService!),
      new ListPlugin(),
      new MentionsPlugin(
        this.events$,
        this.mentionsDataSource$,
        this.mentionsListService!,
        this.scroll$,
      ),
    ];
  }

  public registerToolbarButtons(buttons: PluginsButtons<ConfigPlugins<ProsemirrorDefaultConfig>>) {
    const ctxButtons = [
      buttons.toggleBold,
      buttons.toggleItalic,
      buttons.toggleUnderline,
      buttons.toggleLink,
    ];
    const leftButtons = [
      buttons.toggleBold,
      buttons.toggleItalic,
      buttons.toggleUnderline,
      buttons.toggleBulletList,
      buttons.toggleOrderedList,
      buttons.toggleLink,
    ];

    const rightButtons: EditorButton[] = [buttons.toggleMentionList, buttons.toggleEmojiList];
    this.toolbarService!.setContextButtons(ctxButtons);
    this.toolbarService!.setLeftButtons(leftButtons);
    this.toolbarService!.setRightButtons(rightButtons);
  }

  public ngOnDestroy() {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }
}
